'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { useCustomForm } from '@insta-club/hooks';
import { BottomSheet, Button, Form, Textarea } from '@insta-club/ui';
import { ComposeComment, ComposeCommentFormValues } from '@insta-club/validator';
import { useState } from 'react';
import { Controller, SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Rating } from 'src/libs/components/common';
import { AngryIcon, HappyIcon, LoveIcon, NeutralIcon, SadIcon } from 'src/libs/components/icons';
import { mutate } from 'swr';

const ratingIcons = {
    angry: <AngryIcon />,
    sad: <SadIcon />,
    neutral: <NeutralIcon />,
    happy: <HappyIcon />,
    love: <LoveIcon />
};

const ratingLabels: Record<number, string> = {
    1: 'بد',
    2: 'معمولی',
    3: 'خوب',
    4: 'خیلی خوب',
    5: 'عالی'
};

const customStyles = {
    itemShapes: [ratingIcons.angry, ratingIcons.sad, ratingIcons.neutral, ratingIcons.happy, ratingIcons.love],
    activeFillColor: '#FABF35',
    inactiveFillColor: '#E8E8E8'
};

interface CommentBottomSheetProps {
    entityType: 'course' | 'post' | 'product';
    entityId: string | number;
    parentId?: string | number;
}

function CommentBottomSheet({ entityId, entityType, parentId }: CommentBottomSheetProps) {
    const [openSheet, setOpenSheet] = useState<boolean>(false);
    const form = useCustomForm(ComposeComment);
    const {
        control,
        reset,
        formState: { errors, isLoading }
    } = form;

    const onSubmit: SubmitHandler<ComposeCommentFormValues> = async (data) => {
        try {
            const res = await fetcher(ApiKeys.POST_COMMENT, 'dashboard', 'POST', {
                body: {
                    type: entityType,
                    id: entityId,
                    ...data,
                    ...(parentId && { parent_id: parentId })
                }
            });
            if (res.id) {
                toast.success('دیدگاه شما با موفقیت ایجاد شد');
                setOpenSheet(false);
                mutate(ApiKeys.GET_COMMENTS);
                reset();
            }
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <BottomSheet
            title={'دیدگاهتان را به اشتراک بذارید'}
            open={openSheet}
            onClose={() => setOpenSheet(false)}
            trigger={
                <Button
                    size='lg'
                    fullWidth
                    onClick={() => {
                        setOpenSheet(true);
                    }}
                >
                    ثبت دیدگاه
                </Button>
            }
        >
            <Form form={form} onSubmit={onSubmit}>
                <div className='flex flex-col items-center space-y-4'>
                    <h5 className='text-sm font-semibold text-gray-600'>
                        امتیاز شما به این دوره:{' '}
                        <span className='text-gray-700'>{ratingLabels[form.watch('rate') ?? 1]}</span>
                    </h5>

                    <div>
                        <Controller
                            control={control}
                            name='rate'
                            rules={{
                                validate: (rating) => (rating !== undefined && rating > 0) || 'امتیاز الزامی است'
                            }}
                            render={({ field: { onChange, onBlur, value } }) => (
                                <>
                                    <Rating
                                        className='[&_.rr--on_.mouth]:fill-[#BB8300] [&_.rr--on_.outline]:stroke-[#BB8300]'
                                        isRequired
                                        visibleLabelId='rating_label'
                                        readOnly={false}
                                        style={{ width: 180, margin: '0 auto 8px auto', direction: 'rtl' }}
                                        value={Number(value) ?? 0}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        itemStyles={customStyles}
                                        items={5}
                                        highlightOnlySelected
                                        spaceBetween='medium'
                                        transition='zoom'
                                    />
                                </>
                            )}
                        />
                        {errors.rate && <div className='text-right text-sm text-red-500'>{errors.rate.message}</div>}
                    </div>
                    <Textarea name='body' label='دیدگاه شما' />
                    <Button loading={isLoading} size='lg' fullWidth className='bg-primary-500 mt-2'>
                        ثبت دیدگاه
                    </Button>
                </div>
            </Form>
        </BottomSheet>
    );
}

export default CommentBottomSheet;

